import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Crown, Clock, Check, Target, Trophy, X } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Flag } from "../components/Flag";
import { useAuth } from "../context/AuthContext";
import { Page, Loader, Empty, GoldButton, GhostButton, Card } from "../components/UI";
import { useEventStream } from "../lib/sse";

const DECIDED_LABEL = {
  FEWER_LOST: "fewest lost selections",
  HIGHER_ODDS: "higher total odds",
  EARLIEST_SUBMISSION: "earliest submission (tie-break)",
  SOLE_SURVIVOR: "sole survivor",
};

export default function Strike5TournamentPage() {
  const { id } = useParams();
  const { player } = useAuth();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(() => {
    api.get(`/strike5/${id}`).then(({ data }) => setData(data)).catch(() => setMissing(true));
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("strike5.")) load(); });

  if (missing) return <Page><Empty label="Tournament not found." /></Page>;
  if (!data) return <Loader label="Loading tournament…" />;
  const t = data.tournament;
  const champ = t.champion;

  return (
    <Page>
      <Link to="/strike5" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-6" style={{ transition: "color 0.15s ease" }}>
        <ArrowLeft className="w-4 h-4" /> All tournaments
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-bold">{t.tier} · {t.public_id}</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tight text-slate-50">Strike 5</h1>
          <div className="text-sm text-slate-400 mt-1">Buy-in {t.buy_in} VP · Pot <span className="text-[#E5B842] font-bold">{t.pot} VP</span> · {t.entries_count} entrants · {t.status.replace(/_/g, " ")}</div>
        </div>
      </div>

      {champ && (
        <div data-testid="strike5-champion" className="rounded-2xl foil-border bg-gradient-to-b from-[#1a1407] to-[#070707] p-6 mb-6 flex items-center gap-4">
          <Crown className="w-10 h-10 text-[#E5B842]" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold">Champion — takes {t.pot} VP</div>
            <div className="font-heading text-3xl font-black uppercase text-slate-50">{champ.country && <Flag country={champ.country} className="inline-block mr-1.5 align-middle" />}{champ.display_name}</div>
          </div>
        </div>
      )}

      {t.showcase && t.showcase_note && (
        <div data-testid="strike5-showcase-note" className="rounded-xl bg-[#1a1407]/60 border border-[#E5B842]/40 p-4 mb-6 text-sm text-[#E5B842] font-mono">
          ⓘ {t.showcase_note}
        </div>
      )}

      {data.me?.entered && data.me?.alive && !data.me?.submitted && (
        <SubmitPanel tournamentId={id} deadline={data.me.deadline} onDone={load} />
      )}
      {data.me?.entered && data.me?.alive && data.me?.submitted && (
        <Card className="p-4 mb-6 flex items-center gap-2 text-emerald-400" data-testid="my-coupon-submitted">
          <Check className="w-5 h-5" /> Coupon submitted for round {t.round} ({data.me.coupon?.num_selections} selections, {data.me.coupon?.total_odds_str} odds). Awaiting results…
        </Card>
      )}

      <div className="space-y-8">
        {data.rounds.length === 0 ? <Empty label="Rounds will appear once the tournament starts." /> : data.rounds.map((r) => (
          <section key={r.round} data-testid={`strike5-round-${r.round}`}>
            <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50 mb-4">
              Round {r.round} <span className="text-slate-600 text-lg">· {r.pentads.length} pentad{r.pentads.length > 1 ? "s" : ""}</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {r.pentads.map((p) => <PentadCard key={p.id} p={p} />)}
            </div>
          </section>
        ))}
      </div>
    </Page>
  );
}

function PentadCard({ p }) {
  const resolved = p.status === "RESOLVED";
  return (
    <div data-testid={`pentad-${p.id}`} className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-heading font-bold uppercase text-slate-200 text-sm"><Target className="w-4 h-4 inline mr-1 text-[#E5B842]" />Pentad {p.index}</span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{p.walkover ? "Walkover" : p.status}</span>
      </div>
      {resolved && p.ranking?.length > 0 ? (
        <table className="w-full text-sm" data-testid={`pentad-results-${p.id}`}>
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">
              <th className="text-left py-1 w-6">#</th><th className="text-left py-1">Player</th>
              <th className="text-right py-1">Lost</th><th className="text-right py-1">Odds</th>
            </tr>
          </thead>
          <tbody>
            {p.ranking.map((r) => (
              <tr key={r.player_id} className={`border-t border-[#2b2512]/60 ${r.position === 1 ? "text-[#E5B842]" : "text-slate-300"}`}>
                <td className="py-1.5 font-heading font-black">{r.position === 1 ? <Crown className="w-3.5 h-3.5 inline" /> : r.position}</td>
                <td className="py-1.5 truncate">{r.player?.country && <Flag country={r.player.country} className="inline-block mr-1 align-middle" />}{r.player?.display_name}</td>
                <td className="py-1.5 text-right tabular-nums">{r.excluded ? <span className="text-red-400"><X className="w-3 h-3 inline" />excl</span> : r.lost}</td>
                <td className="py-1.5 text-right tabular-nums font-mono">{r.excluded ? "—" : r.odds?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="space-y-1.5">
          {p.players.map((pl) => (
            <div key={pl?.player_id} className="flex items-center gap-2 text-sm text-slate-300">
              <span className="truncate">{pl?.country && <Flag country={pl.country} className="inline-block mr-1 align-middle" />}{pl?.display_name}</span>
            </div>
          ))}
          {!resolved && <div className="mt-2 text-[11px] font-mono text-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Awaiting coupons & results</div>}
        </div>
      )}
      {resolved && p.qualifier && (
        <div data-testid={`pentad-decided-${p.id}`} className="mt-3 pt-2 border-t border-[#2b2512] text-[11px] font-mono text-emerald-400">
          Qualified: {p.qualifier.display_name} — decided by <span className="text-[#E5B842] font-bold">{p.walkover ? "walkover" : (DECIDED_LABEL[p.decided_by] || p.decided_by || "criteria")}</span>
        </div>
      )}
    </div>
  );
}

function SubmitPanel({ tournamentId, deadline, onDone }) {
  const [events, setEvents] = useState(null);
  const [picks, setPicks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    api.get(`/strike5/${tournamentId}/eligible-events`).then(({ data }) => setEvents(data.events)).catch(() => setEvents([]));
  }, [tournamentId]);

  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.round((new Date(deadline) - new Date()) / 1000)));
    tick(); const i = setInterval(tick, 1000); return () => clearInterval(i);
  }, [deadline]);

  const total = useMemo(() => picks.reduce((a, p) => a * parseFloat(p.odds), 1), [picks]);

  const toggle = (ev, m, s) => {
    setPicks((prev) => {
      if (prev.find((p) => p.selection_id === s.selection_id)) return prev.filter((p) => p.selection_id !== s.selection_id);
      if (prev.length >= 6) { toast.error("Maximum 6 selections"); return prev; }
      return [...prev, { event_id: ev.id, market_id: m.market_id, selection_id: s.selection_id, odds: s.odds, label: s.label, event_label: `${ev.home_name} vs ${ev.away_name}` }];
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/strike5/${tournamentId}/coupon`, { selections: picks.map((p) => ({ event_id: p.event_id, market_id: p.market_id, selection_id: p.selection_id })) });
      toast.success("Strike 5 coupon submitted");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && d.message) || apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 mb-6 foil-border" data-testid="strike5-submit-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-bold uppercase text-slate-50 flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E5B842]" /> Submit your coupon (1–6 selections)</h3>
        <span data-testid="deadline-countdown" className={`font-mono text-sm font-bold ${left < 15 ? "text-red-400" : "text-amber-300"}`}><Clock className="w-4 h-4 inline" /> {left}s</span>
      </div>
      {!events ? <Loader label="Loading events…" /> : events.length === 0 ? <Empty label="No eligible events right now — check back in a moment." /> : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3 max-h-80 overflow-auto pr-1" data-testid="strike5-events">
            {events.map((ev) => (
              <div key={ev.id} className="rounded-lg bg-[#000000] border border-[#2b2512] p-3">
                <div className="font-heading font-bold uppercase text-slate-100 text-sm">{ev.home_name} vs {ev.away_name}</div>
                {ev.markets.map((m) => (
                  <div key={m.market_id} className="mt-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono">{m.name}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {m.selections.map((s) => {
                        const sel = picks.some((p) => p.selection_id === s.selection_id);
                        return (
                          <button key={s.selection_id} data-testid={`s5-sel-${s.selection_id}`} onClick={() => toggle(ev, m, s)}
                            className={`px-2.5 py-1 rounded text-xs font-semibold border ${sel ? "bg-[#E5B842] text-[#000000] border-[#E5B842]" : "bg-[#0f0f0f] text-slate-200 border-[#2b2512] hover:border-[#E5B842]/40"}`}
                            style={{ transition: "border-color 0.15s ease" }}>
                            {s.label} <span className="font-mono">{s.odds}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div>
            <div className="rounded-lg bg-[#000000] border border-[#2b2512] p-4">
              <div className="flex justify-between text-sm"><span className="text-slate-400 font-mono text-[10px] uppercase">Selections</span><span className="text-slate-100">{picks.length}/6</span></div>
              <div className="flex justify-between items-center mt-2"><span className="text-slate-400 font-mono text-[10px] uppercase">Total odds</span><span data-testid="s5-total-odds" className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{total.toFixed(2)}</span></div>
              <GoldButton className="w-full mt-4" data-testid="s5-submit-btn" disabled={picks.length === 0 || busy || left <= 0} onClick={submit}>
                {busy ? "Submitting…" : "Submit coupon"}
              </GoldButton>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
